import * as React from 'react';
import { Box, LinearProgress, Typography, Stack, Divider } from '@mui/material';

// data: {
//   program?: string | null
//   first_name?: string | null
//   last_name?: string | null
//   tutor_id?: number | null
//   program_id?: number | null
//   start_time?: string | null   // e.g., "14:00" (HH:mm)
//   duration?: number | null     // minutes
//   session_date?: string | Date | null // ISO string from time.Time
// }

export default function AssessmentPivotPopover({ data, loading = false }) {
  // ---- helpers ----
  const safe = (v, fallback = '—') =>
    v === undefined || v === null || v === '' ? fallback : v;

  const fmtDate = (d) => {
    if (!d) return '—';
    const dt = typeof d === 'string' ? new Date(d) : d; // handles time.Time -> ISO
    if (Number.isNaN(dt?.getTime?.())) return '—';
    return dt.toLocaleDateString(undefined, {
      year: 'numeric',
      month: 'short',
      day: '2-digit',
    });
  };

  const fmtTimeRange = (startStr, durationMin) => {
    if (!startStr) return '—';
    try {
      // Expecting "HH:mm" (24h). Fallbacks if different.
      const [hh, mm] = String(startStr).split(':').map((n) => parseInt(n, 10));
      if (Number.isNaN(hh) || Number.isNaN(mm)) return startStr;

      const base = new Date();
      base.setHours(hh, mm, 0, 0);

      const end = new Date(base.getTime() + (Number(durationMin || 0) * 60_000));
      const opts = { hour: 'numeric', minute: '2-digit' };

      return `${base.toLocaleTimeString([], opts)} – ${end.toLocaleTimeString([], opts)}`;
    } catch {
      return startStr;
    }
  };

  const fullName = () => {
    const fn = safe(data?.first_name, '').trim();
    const ln = safe(data?.last_name, '').trim();
    return (fn || ln) ? `${fn} ${ln}`.trim() : '—';
  };

  // ---- UI ----
  return (
    <Box sx={{ border: 1, p: 1.25, bgcolor: 'background.paper', borderRadius: 1.5, minWidth: 280 }}>
      {loading && (
        <Box sx={{ mb: 1 }}>
          <LinearProgress color="secondary" />
        </Box>
      )}

      <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
        Session info
      </Typography>

      <Divider sx={{ my: 1 }} />

      <Stack spacing={0.5}>
        <Typography variant="body2">
          <strong>Tutor:</strong> {fullName()} {data?.tutor_id ? `(ID: ${data.tutor_id})` : ''}
        </Typography>

        <Typography variant="body2">
          <strong>Session date:</strong> {fmtDate(data?.session_date)}
        </Typography>

        <Typography variant="body2">
          <strong>Time:</strong> {fmtTimeRange(data?.start_time, data?.duration)}
        </Typography>

        <Typography variant="body2">
          <strong>Program:</strong> {safe(data?.program)} {data?.program_id ? `(ID: ${data.program_id})` : ''}
        </Typography>
      </Stack>
    </Box>
  );
}